/*
 * Copyright (c) 2020. Sonrai Analytics Ltd.
 * Developed by Ryan Duffy
 */
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, of, combineLatest, Subscription, Subject } from 'rxjs';
import { MyNotificationModel } from '@app/models/my-notification.model';
import { MyNotificationListService } from '../api-services/my-notifications/my-notifications-list.service';
import { ProjectModel } from '@app/models/project.model';
import { TaskModel } from '@app/models/task.model';
import { switchMap, map, take } from 'rxjs/operators';
import { ProjectsListService } from '../api-services/projects/projects-list.service';
import { TasksListService } from '../api-services/tasks/tasks-list.service';
import { NoteModel } from '@app/models/note.model';
import { MyNotificationService } from '../api-services/my-notifications/my-notification.service';
import { NotesListService } from '../api-services/notes/notes-list.service';
import { OrganisationCognitoUser } from '@app/models/cognito-organisation-user.model';
import { MaterialSnackbarService } from '@app/services/shared-services/material-snackbar.service';

// Model required to ensure a notification has access to its project-analytics/note as well as the user details of who is invovled
export class DetailedNotificationModel {
  notification: MyNotificationModel;
  assignedBy: OrganisationCognitoUser;
  sourceProject?: ProjectModel;
  sourceTask?: TaskModel;
  sourceNote?: NoteModel;
  userFullName: string;
  isChecked: boolean;
}
// Model to store the full array of all notifications, tasks, notes and projects-list
class FullyDetailedNotifications {
  myNotifications: MyNotificationModel[];
  allProjects?: ProjectModel[];
  allTasks?: TaskModel[];
  allNotes?: NoteModel[];
}

@Injectable({
  providedIn: 'root',
})
export class MyDetailedNotificationsService implements OnDestroy {
  // Subjects required to pass the relevant variables across components in the platform
  // Subject required for passing the count of notifications around components
  public newNotificationCountSubject = new BehaviorSubject<number>(0);
  // Subjects required for passing all notifications and new notifications only
  private notificationDetailsListSubject = new BehaviorSubject<DetailedNotificationModel[]>([]);
  private newNotificationDetailsListSubject = new BehaviorSubject<DetailedNotificationModel[]>([]);
  // Used to set a spinner when first loading notifications
  private fetchingSubject = new Subject<boolean>();
  public fetching$ = this.fetchingSubject.asObservable();
  // Locally stored lists of notifications
  public currentMyNotificationsList: MyNotificationModel[] = []; // List used to determine the fully detailed lists
  public allNotificationList: DetailedNotificationModel[] = []; // All Notifications list to be displayed
  public nonFilteredNotificationList: DetailedNotificationModel[] = []; // All Notifications list to be displayed
  public newNotificationsList: DetailedNotificationModel[] = []; // New Notifications list to be displayed
  public oldNotificationsList: any; // Old Notifications list before transformation

  // Variables required for limiting and determining userDetails
  public displayLimit = 15;
  public userList: OrganisationCognitoUser[];
  public currentUserId: string;

  // Subscriptions
  private fullNotificationsSubscription: Subscription;
  private myNotificationsSubscription: Subscription;
  private idSubscription: Subscription;

  constructor(
    private projectListService: ProjectsListService,
    private tasksListService: TasksListService,
    private myNotificationService: MyNotificationService,
    private myNotificationListService: MyNotificationListService,
    private noteListService: NotesListService,
    private notify: MaterialSnackbarService
  ) {}
  /**
   *
   *
   * @param {MyNotificationModel[]} myNotifications
   * @param {string} userId
   * @param {boolean} [fullFetch]
   * @param {boolean} [baseFetch]
   * @memberof MyDetailedNotificationsService
   */
  getMyNotifications(
    myNotifications: MyNotificationModel[],
    userId: string,
    fullFetch?: boolean,
    baseFetch?: boolean,
    updateFetch?: boolean,
    oldMyNotifications?: any
  ) {
    // temporary lists required
    const tasks: TaskModel[] = [];
    const notes: NoteModel[] = [];
    const userNotifications: MyNotificationModel[] = [];
    const projects: ProjectModel[] = [];
    // Batch of observables required to be passed in via combineLatest
    const observableBatch: Observable<any>[] = [];
    const allNotificationsList: DetailedNotificationModel[] = [];
    const nonFilteredNotificationsList: DetailedNotificationModel[] = [];
    const newNotificationsList: DetailedNotificationModel[] = [];
    // Used to determine the need to retreve all tasks and/or all projects-list
    let projectRetreieve = false;
    let project: ProjectModel;
    let taskRetrieve = false;
    let task: TaskModel;
    let noteRetrieve = false;
    let note: NoteModel;
    let count = this.newNotificationCountSubject.value;
    let detailedNotification: DetailedNotificationModel;
    let detailedNotifications: DetailedNotificationModel[] = [];
    let allData: FullyDetailedNotifications;
    // Get the current count of notifications
    let returnedProjects: ProjectModel[];
    let returnedTasks: TaskModel[];
    let returnedNotes: NoteModel[];
    // SHow a load if it is the first base fetch otherwise it's just checks for new notifications
    if (baseFetch) {
      this.fetchingSubject.next(true);
    }
    // get the passed in notifications
    for (const notification of myNotifications) {
      // ensure they relate to the currently logged in user
      if (notification.userId === userId) {
        userNotifications.push(notification);
        // Ensure they do not already exist in the list - if they do, do not process them in the list
        if (
          !this.currentMyNotificationsList.find(
            (notif) => notif.notificationId === notification.notificationId
          )
        ) {
          this.currentMyNotificationsList.push(notification);
          // Add new notifications to the count
          if (!notification.notificationDetails.notificationRead) {
            count = count + 1;
          }
        }
      }
    }
    this.passNewCount(count);
    observableBatch.push(of(userNotifications));
    // Create an array of obserables of all the notifications relevant, notes, tasks and projects-list
    // The reason for getting a list of all of these is due to the number of calls that may be required to get the notes, projects-list etc.
    // With the nature of the subjects too many calls to this will make all the arrays difficult to be matched up
    //  and slower the more queries occur.
    for (const userNotification of userNotifications) {
      // If any of the notifications are related to projects-list
      // Get a list of all projects-list
      if (userNotification.notificationDetails.notificationType.includes('project')) {
        if (!projectRetreieve) {
          projectRetreieve = true;
          // If there use the local list to get projects-list
          // OR if this is a fetch for further notifications use the DB call to account for discrepancies
          // This will only make the call ONCE and reduce running time despite being a
          // less specific call as there will be MUCH less calls required
          if (!this.projectListService.projectsList.length || fullFetch) {
            this.projectListService.getData({});
            // Pass the full list into the batch
            observableBatch.push(
              this.projectListService.dataSubject.pipe(
                switchMap((fetchProjects) => {
                  for (const fetchProject of fetchProjects) {
                    project = new ProjectModel();
                    project = project.fromJson(fetchProject);
                    projects.push(project);
                  }
                  return of(projects);
                })
              )
            );
          } else {
            // Pass the full list into the batch
            observableBatch.push(of(this.projectListService.projectsList));
          }
        }
        // Same as above for the tasks
      } else if (userNotification.notificationDetails.notificationType.includes('task')) {
        if (!taskRetrieve) {
          taskRetrieve = true;
          if (!this.tasksListService.tasksList.length || fullFetch) {
            this.tasksListService.getData({});
            observableBatch.push(
              this.tasksListService.dataSubject.pipe(
                switchMap((fetchTasks) => {
                  for (const fetchTask of fetchTasks) {
                    task = new TaskModel();
                    task = task.fromJson(fetchTask);
                    tasks.push(task);
                  }
                  return of(tasks);
                })
              )
            );
          } else {
            observableBatch.push(of(this.tasksListService.tasksList));
          }
        }
      } else if (userNotification.notificationDetails.notificationType.includes('note')) {
        if (!noteRetrieve) {
          noteRetrieve = true;
          if (!this.noteListService.notesList.length || fullFetch) {
            this.noteListService.getData({});
            observableBatch.push(
              this.noteListService.dataSubject.pipe(
                switchMap((fetchNotes) => {
                  for (const fetchNote of fetchNotes) {
                    note = new NoteModel();
                    note = note.fromJson(fetchNote);
                    notes.push(note);
                  }
                  return of(notes);
                })
              )
            );
          } else {
            observableBatch.push(of(this.tasksListService.tasksList));
          }
        }
      }
    }
    // Combine all these requests itno one observable so they can be used as one FullData object
    this.fullNotificationsSubscription = combineLatest(observableBatch)
      .pipe(
        map((observables) => {
          if (observables[0].length > 0) {
            // Sort the arrays received into their relevant sections
            for (const returnedList of observables) {
              if (returnedList[0] instanceof ProjectModel) {
                returnedProjects = returnedList;
              } else if (returnedList[0] instanceof NoteModel) {
                returnedNotes = returnedList;
              } else if (returnedList[0] instanceof TaskModel) {
                returnedTasks = returnedList;
              }
            }
            allData = {
              myNotifications: observables[0],
              allProjects: returnedProjects,
              allTasks: returnedTasks,
              allNotes: returnedNotes,
            };
            return allData;
          } else {
            return null;
          }
        })
      )
      // Get all the lists - notifications, projects-list and tasks and generate fullyDetailedNotifcationsList from this
      .subscribe((fullData) => {
        if (fullData) {
          // For all the carried accross notifications
          fullData.myNotifications.forEach(notification => {
            detailedNotification = new DetailedNotificationModel();
            detailedNotification.notification = notification;
            // If there are projects-list - assign this notification it's relevant project-analytics or none if not found
            if (fullData.allProjects) {
              detailedNotification.sourceProject =
                fullData.allProjects.find(
                  (projectFind) =>
                    projectFind.projectId === notification.notificationDetails.notificationSourceId
                ) || new ProjectModel();
            }
            // If there are tasks - assign this notification it's relevant task or none if not found
            if (fullData.allTasks) {
              detailedNotification.sourceTask =
                fullData.allTasks.find(
                  (taskFind) =>
                    taskFind.taskId === notification.notificationDetails.notificationSourceId
                ) || new TaskModel();
            }
            // If there are notes - assign this notification it's relevant note or none if not found
            if (fullData.allNotes) {
              detailedNotification.sourceNote =
                fullData.allNotes.find(
                  (noteFind) =>
                    noteFind.noteId === notification.notificationDetails.notificationSourceId
                ) || new NoteModel();
            }
            // Get the details of the user that created this notification
            detailedNotification.assignedBy =
              this.userList.find(
                (user) => user.userId === notification.notificationDetails.ownerId
              ) || OrganisationCognitoUser.returnNoUserModel();
            detailedNotification.userFullName = `${detailedNotification.assignedBy.firstName}
            ${detailedNotification.assignedBy.familyName}`;
            // Only add this notification if it currently does not exist
            // Add to the all notifications list
            // Ensure to only add on the notifications up to the display limit and don't allow duplicates

            // Only add this notification if it currently does not exist
            if (!detailedNotification.notification.notificationDetails.notificationRead) {
              // Add to the new notifications list
              newNotificationsList.push(detailedNotification);
            }
            detailedNotifications.push(detailedNotification);

          });
        }
        // Sort notifications by created date
        detailedNotifications.sort((a,b)=> (a.notification.notificationDetails.created < b.notification.notificationDetails.created ? 1 : -1));
        // Push notifications by display limit amount
        for (let index = 0; index < detailedNotifications.length; index++) {
          // Set isChecked value
          const notification = detailedNotifications[index];
          if (index <= this.displayLimit-1) {
            allNotificationsList.push(notification);
          }
          nonFilteredNotificationsList.push(notification);
        }
        this.nonFilteredNotificationList = nonFilteredNotificationsList;
        if (!updateFetch) {
          // Once the lists are populated - sort them and pass them to the subject so
          // they can be displayed with the subject or the local list
          this.passUpdatedNewList(this.sortArray(newNotificationsList));
          this.passUpdatedList(this.sortArray(allNotificationsList));
          this.oldNotificationsList = oldMyNotifications;
        } else {
          this.passUpdatedNewList(
            this.sortArray([...newNotificationsList, ...this.newNotificationsList])
          );
          this.passUpdatedList(
            this.sortArray([...allNotificationsList, ...this.allNotificationList])
          );
        }
        this.fetchingSubject.next(false);
        if (this.fullNotificationsSubscription) {
          this.fullNotificationsSubscription.unsubscribe();
        }
      });
  }
  /**
   *
   *
   * @returns {Observable<DetailedNotificationModel[]>}
   * @memberof MyDetailedNotificationsService
   */
  getNotificationList(): Observable<DetailedNotificationModel[]> {
    return this.notificationDetailsListSubject.asObservable();
  }
  /**
   *
   *
   * @returns {Observable<DetailedNotificationModel[]>}
   * @memberof MyDetailedNotificationsService
   */
  getNewNotificationList(): Observable<DetailedNotificationModel[]> {
    return this.newNotificationDetailsListSubject.asObservable();
  }
  /**
   *
   *
   * @returns {Observable<number>}
   * @memberof MyDetailedNotificationsService
   */
  getNewCount(): Observable<number> {
    return this.newNotificationCountSubject.asObservable();
  }
  /**
   *
   *
   * @param {DetailedNotificationModel[]} newList
   * @memberof MyDetailedNotificationsService
   */
  passUpdatedList(newList: DetailedNotificationModel[]): void {
    this.notificationDetailsListSubject.next(newList);
    this.allNotificationList = newList;
  }
  /**
   *
   *
   * @param {DetailedNotificationModel[]} newList
   * @memberof MyDetailedNotificationsService
   */
  passUpdatedNewList(newList: DetailedNotificationModel[]): void {
    this.newNotificationDetailsListSubject.next(newList);
    this.newNotificationsList = newList;
  }
  /**
   *
   *
   * @param {number} number
   * @memberof MyDetailedNotificationsService
   */
  passNewCount(number: number): void {
    this.newNotificationCountSubject.next(number);
  }

  /**
   *  checks for new notifications when required and add them to a list
   *
   * @memberof MyDetailedNotificationsService
   */
  checkForNewNotifications(userId: string): Observable<MyNotificationModel[]> {
    let newNotification: MyNotificationModel;
    const newNotifications: MyNotificationModel[] = [];
    return new Observable<MyNotificationModel[]>((observer) => {
      this.idSubscription = this.myNotificationService.dataSubject
        .pipe(take(1))
        .subscribe((notifications) => {
          this.idSubscription.unsubscribe();
          for (const notification of notifications) {
            newNotification = new MyNotificationModel();
            newNotification = newNotification.fromJson(notification);
            // Check if it exists in the currently displayed list
            if (
              !this.currentMyNotificationsList.find(
                (notif) => notif.notificationId === newNotification.notificationId
              )
            ) {
              // If not currently in the list add it to the local lists required
              newNotifications.push(newNotification);
            }
          }
          observer.next(newNotifications);
          observer.complete();
          // Pass the list of new notifications for this user so they can be added to the list and update the number
        });
      this.myNotificationService.getMyNewNotificationByUserId(userId);
    });
  }

  /**
   *  sort an array of detailed notifications by created date
   *
   * @param {DetailedNotificationModel[]} list
   * @returns {DetailedNotificationModel[]}
   * @memberof MyDetailedNotificationsService
   */
  sortArray(list: DetailedNotificationModel[]): DetailedNotificationModel[] {
    return list.sort(
      (a, b) =>
        new Date(b.notification.notificationDetails.created).getTime() -
        new Date(a.notification.notificationDetails.created).getTime()
    );
  }
  /**
   *  when the user scrolls we need to increase the limit to be displayed by 15, this will add to the limit
   *
   * @memberof MyDetailedNotificationsService
   */
  increaseDisplayLimit() {
    this.displayLimit = this.displayLimit + 15;
  }
  /**
   *
   *
   * @param {DetailedNotificationModel} notification
   * @memberof MyDetailedNotificationsService
   */
  removeFromAllList(notification: DetailedNotificationModel) {
    let index = 0;
    for (const notif of this.allNotificationList) {
      if (notif.notification.notificationId === notification.notification.notificationId) {
        this.allNotificationList.splice(index, 1);
      }
      index++;
    }
    this.passUpdatedList(this.allNotificationList);
  }
  /**
   *
   *
   * @param {DetailedNotificationModel} notification
   * @memberof MyDetailedNotificationsService
   */
  addtoNewList(notification: DetailedNotificationModel) {
    this.newNotificationsList.push(notification);
    this.newNotificationsList = this.sortArray(this.newNotificationsList);
    this.passUpdatedNewList(this.newNotificationsList);
  }
  /**
   *
   *
   * @param {DetailedNotificationModel} notification
   * @memberof MyDetailedNotificationsService
   */
  removeFromNewList(notification: DetailedNotificationModel) {
    let index = 0;
    for (const notif of this.newNotificationsList) {
      if (notif.notification.notificationId === notification.notification.notificationId) {
        this.newNotificationsList.splice(index, 1);
      }
      index++;
    }
    this.passUpdatedNewList(this.newNotificationsList);
  }
  /**
   *  update an item in the new list
   *
   * @param {DetailedNotificationModel} notification
   * @memberof MyDetailedNotificationsService
   */
  updateNewList(notification: DetailedNotificationModel) {
    for (let notif of this.newNotificationsList) {
      if (notif.notification.notificationId === notification.notification.notificationId) {
        notif = notification;
      }
    }
    this.passUpdatedNewList(this.newNotificationsList);
  }

  ngOnDestroy(): void {
    if (this.fullNotificationsSubscription) {
      this.fullNotificationsSubscription.unsubscribe();
    }
    if (this.myNotificationsSubscription) {
      this.myNotificationsSubscription.unsubscribe();
    }
    if (this.idSubscription) {
      this.idSubscription.unsubscribe();
    }
  }
}
